import React, { useState, useRef, useEffect } from 'react';
import { Calendar } from 'lucide-react';

const CustomDatePicker = ({ value, onChange, required }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showYearPicker, setShowYearPicker] = useState(false);
  const pickerRef = useRef(null);

  const selectedDate = value ? new Date(value) : null;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { firstDay, daysInMonth };
  };

  const formatDisplayDate = (date) => {
    if (!date) return 'Select date';
    return new Date(date).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const handleDateClick = (day) => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    onChange({ target: { name: 'date', value: newDate.toISOString().slice(0, 10) } });
    setShowPicker(false);
  };

  const changeMonth = (delta) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + delta, 1));
  };

  const changeYear = (year) => {
    setCurrentMonth(new Date(year, currentMonth.getMonth(), 1));
    setShowYearPicker(false);
  };

  const getYearRange = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 50; i <= currentYear + 50; i++) {
      years.push(i);
    }
    return years;
  };

  const { firstDay, daysInMonth } = getDaysInMonth(currentMonth);
  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  const isSelectedDay = (day) => {
    if (!selectedDate || !day) return false;
    return selectedDate.getDate() === day &&
           selectedDate.getMonth() === currentMonth.getMonth() &&
           selectedDate.getFullYear() === currentMonth.getFullYear();
  };

  const isToday = (day) => {
    if (!day) return false;
    const today = new Date();
    return today.getDate() === day &&
           today.getMonth() === currentMonth.getMonth() &&
           today.getFullYear() === currentMonth.getFullYear();
  };

  return (
    <div style={{ position: 'relative' }} ref={pickerRef}>
      <div
        onClick={() => setShowPicker(!showPicker)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 12px',
          border: '1px solid #E0E5F2',
          borderRadius: '8px',
          cursor: 'pointer',
          background: '#fff',
          fontSize: '14px',
          color: value ? '#2B3674' : '#707EAE'
        }}
      >
        <span>{formatDisplayDate(value)}</span>
        <Calendar size={18} color="#707EAE" />
      </div>

      {showPicker && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          marginTop: '4px',
          background: '#fff',
          border: '1px solid #E0E5F2',
          borderRadius: '12px',
          padding: '16px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          zIndex: 1000,
          minWidth: '280px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <button
              type="button"
              onClick={() => changeMonth(-1)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '18px',
                cursor: 'pointer',
                color: '#1D19E4',
                padding: '4px 8px'
              }}
            >
              ‹
            </button>
            <span 
              onClick={() => setShowYearPicker(!showYearPicker)}
              style={{ 
                fontSize: '14px', 
                fontWeight: '600', 
                color: '#2B3674',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '6px',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = '#F4F7FE'}
              onMouseLeave={(e) => e.target.style.background = 'transparent'}
            >
              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
            <button
              type="button"
              onClick={() => changeMonth(1)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '18px',
                cursor: 'pointer',
                color: '#1D19E4',
                padding: '4px 8px'
              }}
            >
              ›
            </button>
          </div>

          {showYearPicker ? (
            <div style={{
              maxHeight: '200px',
              overflowY: 'auto',
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '8px'
            }}>
              {getYearRange().map(year => (
                <div
                  key={year}
                  onClick={() => changeYear(year)}
                  style={{
                    textAlign: 'center',
                    padding: '8px',
                    fontSize: '13px',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    background: year === currentMonth.getFullYear() ? '#1D19E4' : 'transparent',
                    color: year === currentMonth.getFullYear() ? '#fff' : '#2B3674',
                    fontWeight: year === currentMonth.getFullYear() ? '600' : '400',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (year !== currentMonth.getFullYear()) {
                      e.target.style.background = '#F4F7FE';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (year !== currentMonth.getFullYear()) {
                      e.target.style.background = 'transparent';
                    }
                  }}
                >
                  {year}
                </div>
              ))}
            </div>
          ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '4px'
          }}>
            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
              <div key={day} style={{
                textAlign: 'center',
                fontSize: '11px',
                fontWeight: '600',
                color: '#707EAE',
                padding: '8px 0'
              }}>
                {day}
              </div>
            ))}
            {days.map((day, idx) => (
              <div
                key={idx}
                onClick={() => day && handleDateClick(day)}
                style={{
                  textAlign: 'center',
                  padding: '8px',
                  fontSize: '13px',
                  cursor: day ? 'pointer' : 'default',
                  borderRadius: '8px',
                  background: isSelectedDay(day) ? '#1D19E4' : isToday(day) ? '#F4F7FE' : 'transparent',
                  color: isSelectedDay(day) ? '#fff' : '#2B3674',
                  fontWeight: isSelectedDay(day) || isToday(day) ? '600' : '400',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (day && !isSelectedDay(day)) {
                    e.target.style.background = '#F4F7FE';
                  }
                }}
                onMouseLeave={(e) => {
                  if (day && !isSelectedDay(day)) {
                    e.target.style.background = isToday(day) ? '#F4F7FE' : 'transparent';
                  }
                }}
              >
                {day || ''}
              </div>
            ))}
          </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomDatePicker;
